// ============================================================================
// SETUP (ADR-0048 §3, Slice 2) — the tenant-level readiness view: one card per
// entity the user can access, each with its stages (Company → Books → Tax →
// Invoicing → Banking). Setup state is entity-scoped; business-unit items sit
// inside their entity's stage and name the unit. Nothing is ticked by hand: the
// server computes every item from the state that exists, so a representative
// and a self-onboarding tenant see the same page in any order they configure
// things. An item of another entity asks before switching the active entity.
// Keys are documented in fairbooks-doc/reference/required-configuration.md.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { ConfirmDialog } from 'components/modals';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { setupService } from 'api/services';
import type { SetupCapabilityDto, SetupCheckDto, SetupReadinessDto, SetupTenantReadinessDto } from 'api/types';
import { useAuthContext } from 'contexts/AuthContext';
import { useNavigation } from 'contexts/NavigationContext';

interface SetupChecklistScreenProps { moduleIcon?: string; tabId?: string; moduleName?: string; }

const statusStyle = (s: SetupCheckDto['status']): { color: string; label: string; icon: keyof typeof IconSystem } => {
  switch (s) {
    case 'OK': return { color: 'var(--success-color, #16a34a)', label: 'Done', icon: 'checkCircle' };
    case 'MISSING': return { color: 'var(--danger-color, #dc2626)', label: 'Missing', icon: 'alert' };
    case 'WARNING': return { color: 'var(--warning-text, #b45309)', label: 'Check', icon: 'warning' };
    case 'OPTIONAL': return { color: 'var(--text-muted, #64748b)', label: 'Optional', icon: 'info' };
    default: return { color: 'var(--text-muted, #64748b)', label: 'Not needed', icon: 'xCircle' };
  }
};

const Icon: React.FC<{ name: keyof typeof IconSystem; size?: number }> = ({ name, size = 16 }) => {
  const C = (IconSystem as any)[name] || IconSystem.info;
  return <C size={size} />;
};

const SetupChecklistScreen: React.FC<SetupChecklistScreenProps> = ({ moduleIcon, tabId }) => {
  const { selectedEntity } = useAuthContext();
  const { openScreen, switchEntityAndOpen } = useNavigation();
  const [data, setData] = useState<SetupTenantReadinessDto | null>(null);
  const [loading, setLoading] = useState(false);
  /** Expanded entity cards (by entity id) and expanded stages (by `${entityId}:${code}`). */
  const [openEntities, setOpenEntities] = useState<Set<string>>(new Set());
  const [openStages, setOpenStages] = useState<Set<string>>(new Set());
  const [pendingSwitch, setPendingSwitch] = useState<{ entityId: string; entityName: string; screenCode: string; title: string } | null>(null);
  const [switching, setSwitching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await setupService.getTenantReadiness();
      setData(r);
      // Entities with something to do open; the active entity always opens. Within an open
      // entity, complete stages start collapsed so the eye lands on what is left.
      const entities = new Set<string>();
      const stages = new Set<string>();
      for (const e of r.entities) {
        if (e.missingRequiredCount > 0 || e.entityId === selectedEntity?.id) entities.add(e.entityId);
        for (const c of e.capabilities) {
          if (!(c.ready && c.checks.every(ch => ch.status !== 'WARNING'))) stages.add(`${e.entityId}:${c.code}`);
        }
      }
      setOpenEntities(entities);
      setOpenStages(stages);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not evaluate the setup.');
      setData(null);
    } finally { setLoading(false); }
  }, [selectedEntity?.id]);

  useEffect(() => { load(); }, [load]);

  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) =>
    setter(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const actionButtons: ActionButton[] = [{ ...actionButtonFactory.refresh(load) }];

  // Not-ready entities first, then the active one, then by code.
  const entities = useMemo(() => {
    const list = [...(data?.entities ?? [])];
    list.sort((a, b) => {
      const am = a.missingRequiredCount > 0 ? 0 : 1, bm = b.missingRequiredCount > 0 ? 0 : 1;
      if (am !== bm) return am - bm;
      const aa = a.entityId === selectedEntity?.id ? 0 : 1, ba = b.entityId === selectedEntity?.id ? 0 : 1;
      if (aa !== ba) return aa - ba;
      return (a.entityCode || '').localeCompare(b.entityCode || '');
    });
    return list;
  }, [data, selectedEntity?.id]);

  const total = data?.totalMissingRequiredCount ?? 0;
  const readyEntities = entities.filter(e => e.missingRequiredCount === 0).length;

  const openItem = (entity: SetupReadinessDto, c: SetupCheckDto) => {
    if (!c.screenCode) return;
    if (!selectedEntity || entity.entityId === selectedEntity.id) { openScreen(c.screenCode); return; }
    setPendingSwitch({ entityId: entity.entityId, entityName: entity.entityName || entity.entityCode || 'the entity', screenCode: c.screenCode, title: c.title });
  };

  const confirmSwitch = async () => {
    if (!pendingSwitch) return;
    setSwitching(true);
    try {
      await switchEntityAndOpen(pendingSwitch.entityId, pendingSwitch.entityName, pendingSwitch.screenCode);
    } finally {
      setSwitching(false);
      setPendingSwitch(null);
    }
  };

  const renderCheck = (entity: SetupReadinessDto, c: SetupCheckDto) => {
    const st = statusStyle(c.status);
    const foreign = !!selectedEntity && entity.entityId !== selectedEntity.id;
    return (
      <div key={c.key} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.6rem 0.75rem 0.6rem 1.5rem', borderTop: '1px solid var(--border-color, #e2e8f0)' }}>
        <span style={{ color: st.color, marginTop: 2 }}><Icon name={st.icon} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{c.title}</span>
            <span style={{ fontSize: '0.7rem', color: st.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{st.label}{c.required ? '' : ' · optional'}</span>
            <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{c.key}</span>
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{c.message}</div>
          {c.permission && c.status !== 'OK' && (
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>Needs {c.permission}</div>
          )}
        </div>
        {c.screenCode && c.status !== 'NOT_APPLICABLE' && (
          <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
            title={foreign ? `Switches you to ${entity.entityName || entity.entityCode} first` : undefined}
            onClick={() => openItem(entity, c)}>
            {c.status === 'OK' ? 'View' : 'Open'}{foreign ? ' ↗' : ''}
          </button>
        )}
      </div>
    );
  };

  const renderStage = (entity: SetupReadinessDto, cap: SetupCapabilityDto, index: number) => {
    const key = `${entity.entityId}:${cap.code}`;
    const open = openStages.has(key);
    const warnings = cap.checks.filter(c => c.status === 'WARNING').length;
    return (
      <div key={cap.code} style={{ borderTop: '1px solid var(--border-color, #e2e8f0)' }}>
        <div role="button" tabIndex={0} onClick={() => toggleSet(setOpenStages, key)} onKeyDown={e => { if (e.key === 'Enter') toggleSet(setOpenStages, key); }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', cursor: 'pointer', background: cap.ready ? 'var(--success-bg, #f0fdf4)' : 'var(--bg-secondary, #f8fafc)' }}>
          <span style={{ width: 24, height: 24, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.75rem', flexShrink: 0,
            background: cap.ready ? 'var(--success-color, #16a34a)' : 'var(--danger-color, #dc2626)', color: '#fff' }}>
            {cap.ready ? <IconSystem.check size={12} /> : index + 1}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{cap.title}</div>
            <div style={{ fontSize: '0.775rem', color: 'var(--text-secondary)' }}>{cap.description}</div>
          </div>
          <div style={{ fontSize: '0.775rem', color: cap.ready ? 'var(--success-color, #16a34a)' : 'var(--danger-color, #dc2626)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {cap.ready ? 'Ready' : cap.missingRequiredCount > 0 ? `${cap.missingRequiredCount} missing` : 'Waiting on an earlier stage'}
            {warnings > 0 && <span style={{ color: 'var(--warning-text, #b45309)', marginLeft: '0.5rem' }}>· {warnings} to check</span>}
          </div>
          <span style={{ color: 'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
        </div>
        {open && cap.checks.map(c => renderCheck(entity, c))}
        {open && cap.checks.length === 0 && (
          <div style={{ padding: '0.6rem 1.5rem', fontSize: '0.8125rem', color: 'var(--text-muted)', borderTop: '1px solid var(--border-color, #e2e8f0)' }}>No checks for this stage yet.</div>
        )}
      </div>
    );
  };

  const renderEntity = (entity: SetupReadinessDto) => {
    const open = openEntities.has(entity.entityId);
    const active = entity.entityId === selectedEntity?.id;
    const ready = entity.missingRequiredCount === 0;
    return (
      <div key={entity.entityId} style={{ border: `1px solid ${active ? 'var(--primary-color, #2563eb)' : 'var(--border-color, #e2e8f0)'}`, borderRadius: '0.75rem', background: 'var(--bg-primary, #fff)', overflow: 'hidden', flexShrink: 0 }}>
        <div role="button" tabIndex={0} onClick={() => toggleSet(setOpenEntities, entity.entityId)} onKeyDown={e => { if (e.key === 'Enter') toggleSet(setOpenEntities, entity.entityId); }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', cursor: 'pointer' }}>
          <span style={{ color: ready ? 'var(--success-color, #16a34a)' : 'var(--danger-color, #dc2626)' }}>
            {ready ? <IconSystem.checkCircle size={20} /> : <IconSystem.alert size={20} />}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
              {entity.entityName || entity.entityCode}
              <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400 }}>{entity.entityCode}</span>
              {active && <span className="badge" style={{ fontSize: '0.65rem', background: 'var(--primary-bg, #dbeafe)', color: 'var(--primary-color, #1d4ed8)' }}>current</span>}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: 4 }}>
              {entity.capabilities.map(c => (
                <span key={c.code} style={{ fontSize: '0.7rem', padding: '0.1rem 0.45rem', borderRadius: 999,
                  background: c.ready ? 'var(--success-bg, #f0fdf4)' : 'var(--danger-bg, #fee2e2)',
                  color: c.ready ? 'var(--success-color, #15803d)' : 'var(--danger, #b91c1c)', fontWeight: 600 }}>
                  {c.title}{c.ready ? '' : c.missingRequiredCount > 0 ? ` ${c.missingRequiredCount}` : ' ·'}
                </span>
              ))}
            </div>
          </div>
          <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: ready ? 'var(--success-color, #16a34a)' : 'var(--danger-color, #dc2626)', whiteSpace: 'nowrap' }}>
            {ready ? 'Ready' : `${entity.missingRequiredCount} missing`}
          </div>
          <span style={{ color: 'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
        </div>
        {open && entity.capabilities.map((cap, i) => renderStage(entity, cap, i))}
      </div>
    );
  };

  return (
    <EnterpriseScreenShell
      title="Setup"
      description="Everything each entity needs before a part of FairBooks can be used — computed from what is configured, by stage"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.checkCircle size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
    >
      {/* .data-grid-container is the shell's scrolling slot (flex: 1, min-height: 0). It is a flex
          column by default, which would SHRINK the cards to fit instead of overflowing, so it is
          switched to block flow here and an inner column lays the cards out. */}
      <div className="data-grid-container" style={{ display: 'block', overflowY: 'auto', overflowX: 'hidden', padding: '0 1rem 1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0.75rem 1rem', borderRadius: '0.75rem', flexShrink: 0,
          background: total === 0 ? 'var(--success-bg, #f0fdf4)' : 'var(--warning-bg, #fef3c7)', border: '1px solid var(--border-color, #e2e8f0)' }}>
          <span style={{ color: total === 0 ? 'var(--success-color, #16a34a)' : 'var(--warning-text, #b45309)' }}>
            {total === 0 ? <IconSystem.checkCircle size={20} /> : <IconSystem.warning size={20} />}
          </span>
          <div style={{ flex: 1, fontSize: '0.875rem' }}>
            {loading && !data ? 'Evaluating…'
              : entities.length === 0 ? 'No entities in your scope.'
              : total === 0
                ? <>Every required setting is in place across <strong>{entities.length}</strong> {entities.length === 1 ? 'entity' : 'entities'}.</>
                : <><strong>{total}</strong> required setting{total === 1 ? '' : 's'} still missing across {entities.length} {entities.length === 1 ? 'entity' : 'entities'} ({readyEntities} ready). Open an entity, work down its stages; each item opens the screen it is set on.</>}
          </div>
          {data && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Checked {new Date(data.evaluatedAt).toLocaleTimeString()}</div>}
        </div>

        {entities.map(renderEntity)}

        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          Nothing here is ticked by hand: an item turns green the moment its setting exists. A required item that is missing blocks the stage and every
          screen that depends on it; optional items never block. Business-unit items sit inside their entity and name the unit. An item of another
          entity switches you to that entity first (it asks). The complete register of required configuration lives in the documentation
          (reference / required-configuration).
        </div>
      </div>
      </div>

      <ConfirmDialog
        open={!!pendingSwitch}
        title={`Switch to ${pendingSwitch?.entityName || 'this entity'}?`}
        message={`"${pendingSwitch?.title || 'This setting'}" belongs to ${pendingSwitch?.entityName || 'another entity'}. Switching makes it your working entity: open tabs close and the screen opens there. Your current entity stays as it is.`}
        confirmLabel="Switch and open"
        cancelLabel="Stay here"
        variant="default"
        loading={switching}
        loadingMessage="Switching…"
        onConfirm={confirmSwitch}
        onCancel={() => setPendingSwitch(null)}
      />
    </EnterpriseScreenShell>
  );
};

export default SetupChecklistScreen;
